import { Globals } from "./Globals";
import { getsessioninfo } from "../utils/getsessioninfo.js";
import { PM_Image_Loaded } from "../utils/PM_Image_Loaded.js";
import { PM_Image_Not_Found } from "../utils/PM_Image_Not_Found";
import { PM_Handle_PicQueue_Start } from "../utils/PM_Handle_PicQueue_Start.js";
import { QGenericIndex } from "./QGenericIndex";
import { QPicRef } from "../utils/QPicRef";
import { QRFC } from "./QRFC";
import { getBase64Image } from "../utils/getBase64Image";

// The server decodes each query value once more after parsing it, and signs and
// uses what that leaves. Escaping "%" once more is what makes it arrive as sent.
function EscapePercent(value) {
  return String(value).replace(/%/g, "%25");
}

// A stored local name may be URL-encoded. A "%" that starts no escape is a
// literal character, not an encoding, so the name is used as it is.
function DecodePictureName(name) {
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
}

export class QPictureMgr {
  constructor() {
    this.fIndex = null;
    this.fWSMgr = null;

    this.fPictureFault = 0;

    // key -> subscriber list, for every picture from the moment it is queued
    // until it loads. Queued counts as loading: a second request for a picture
    // still waiting in the queue would otherwise queue a duplicate download.
    this.fPendingIndex = null;
    this.fPicLoadCount = null;

    this.fPicQueue = null;
    this.fPicActiveLoadCount = 0;
    this.fPicLoadDirection = true;
    this.fPausePicQueue = false;
    this.fParentDoc = null;
  }

  InitPictureMgr(wsMgr, parentDoc) {
    this.fIndex = new QGenericIndex();
    this.fIndex.InitIndex();

    this.fReloadIndex = [];

    this.fParentDoc = parentDoc;

    this.fPendingIndex = new Map();
    this.fPicActiveLoadCount = 0;
    this.fPicQueue = [];

    this.fWSMgr = wsMgr;
  }

  PausePicQueue() {
    this.fPausePicQueue = true;
  }

  IsCurrentDoc() {
    var docMgr = this.fWSMgr.GetDocMgr();
    var currentDoc = docMgr.GetCurrentDoc();
    return currentDoc == this.fParentDoc;
  }

  ForceReload(theDirectory, theFile) {
    var key = this.GetKey(theDirectory, theFile);
    var index = this.fReloadIndex.indexOf(key);
    if (index != -1) {
      this.fReloadIndex.splice(index, 1);
      return true;
    }

    return false;
  }

  MarkForReload(theDirectory, theFile) {
    var key = this.GetKey(theDirectory, theFile);
    this.fReloadIndex.push(key);
  }

  FindPicture(theDirectory, theFile) {
    var key = this.GetKey(theDirectory, theFile);
    return this.fIndex.FindObject(key);
  }

  RemovePicture(theDirectory, theFile) {
    this.MarkForReload(theDirectory, theFile);
    var key = this.GetKey(theDirectory, theFile);
    var index = this.fIndex.FindObjectIndex(key);

    if (index != -1) this.fIndex.RemoveIndexObj(index);
  }

  LoadSubscribers(
    theDirectory,
    theFile,
    curCallBack // AddPicture
  ) {
    var key = this.GetKey(theDirectory, theFile);
    var subscriberList = this.fPendingIndex.get(key);
    if (subscriberList) {
      this.fPendingIndex.delete(key);

      var n = subscriberList.length;
      var needRedraw = false;
      // Which picture finished, so a subscriber that is not a chart box (the
      // profile card) can look it up the way an ImageLoaded callback does.
      var loadedRef = { fDirectory: theDirectory, fFileName: theFile };
      //   if (n > 0)
      //    console.log("ClearLoading:", theFile, " subscribers:", n);
      for (var i = 0; i < n; i++) {
        var callBack = subscriberList[i];
        if (callBack != curCallBack) {
          needRedraw = true;
          callBack?.SubscriberImageLoaded(loadedRef);
        }
      }

      if (needRedraw) {
        this.fWSMgr.PrepRenderWS();
        this.fWSMgr.RenderWS();
      }
    }
  }

  // 2.1.3
  AddPicture(
    theDirectory,
    theFile,
    theImage // AddPicture
  ) {
    var key = this.GetKey(theDirectory, theFile);

    // Interesting use case... If the picture is used multiple times it could be loaded multiple times
    // because the asynchronous load has not completed before the next load of the same images is requested...
    var exists = this.FindPicture(theDirectory, theFile);
    if (exists == null) {
      this.fIndex.AddToIndex(theImage, key);
      // console.log("AddPicture:" + key)
    }
  }

  GetKey(theDirectory, theFile) {
    var key = theDirectory + "|" + theFile;
    return key;
  }

  PictureLoading(theDirectory, theFile, callBack) {
    var key = this.GetKey(theDirectory, theFile);
    var subscriberList = this.fPendingIndex.get(key);
    var pictureLoading = subscriberList !== undefined;

    if (pictureLoading) {
      var cbIndex = subscriberList.indexOf(callBack);
      if (cbIndex == -1) {
        subscriberList.push(callBack);
        this.PromoteQueuedPicture(key);
      }
    }

    return pictureLoading;
  }

  // A picture someone has just asked for again moves to the front of the queue,
  // which runs newest first, as a fresh request for it used to. Only on a new
  // subscriber, so a redraw re-asking for the same picture costs no queue scan.
  PromoteQueuedPicture(key) {
    var pq = this.fPicQueue;
    for (var i = pq.length - 1; i >= 0; i--) {
      if (this.GetKey(pq[i]["directory"], pq[i]["imagename"]) === key) {
        if (i !== pq.length - 1) pq.push(pq.splice(i, 1)[0]);
        return;
      }
    }
  }

  LoadPicture(theDirectory, theFile, callBack, destPanel, srcElement, staticImage, clearCache) {
    var picURL = null;
    var anImage = this.FindPicture(theDirectory, theFile);
    var curOrg = this.fWSMgr.GetAccountName();
    var curUser = this.fWSMgr.GetUserName();
    var doc = this.fParentDoc;
    if (anImage == null) {
      var apiServer = Globals._accountInfo.GetAPIServer();
      // Temp holds URL photos: the record's own value, byte for byte the
      // reference the photo preload fetches and caches. Other directories hold
      // local files, whose stored names may be URL-encoded, so they are decoded
      // as they always were.
      var fileValue = theDirectory === "Temp" ? theFile : DecodePictureName(theFile);

      var newCRC = new QRFC();
      newCRC.fDoChecksum = true;
      newCRC.AddParameter("directory", theDirectory);
      newCRC.AddParameter("filename", fileValue);
      newCRC.AddParameter("org", curOrg);
      newCRC.AddParameter("user", curUser);
      var urlParams = new URLSearchParams({
        org: curOrg,
        user: curUser,
        directory: EscapePercent(theDirectory),
        filename: EscapePercent(fileValue)
      });
      var isMaster = callBack && callBack.fLayer === 1;
      if (staticImage) {
        urlParams.set("staticimage", "true");
        newCRC.AddParameter("staticimage", "true");
      }
      if (clearCache) {
        urlParams.set("ccache", "true");
        newCRC.AddParameter("ccache", "true");
      }
      if (isMaster) {
        urlParams.set("isMaster", "true");
        newCRC.AddParameter("isMaster", "true");
      }

      if (doc.GetChartID() != null) {
        var chartID = doc.GetChartID();
        urlParams.set("chartID", chartID);
        newCRC.AddParameter("chartID", chartID);
      }

      var d = new Date();
      var v = d.getTime(); // Overrides the browser caching
      newCRC.AddParameter("v", v);
      urlParams.set("v", v);
      urlParams.set("iv", newCRC.GetParams()["iv"]);
      picURL = urlParams.toString() + getsessioninfo();
      if (apiServer != null) picURL = apiServer + "/ServerSide/qdownloadimage?" + picURL;
      else picURL = "/ServerSide/qdownloadimage?" + picURL;

      anImage = new Image();
    }

    var pRef = new QPicRef();
    pRef.fWSMgr = this.fWSMgr;
    pRef.fPictureMgr = this;
    pRef.fCurOrg = curOrg;
    pRef.fDirectory = theDirectory;
    pRef.fFileName = theFile;
    pRef.fCallBack = callBack;
    pRef.fDestPanel = destPanel;
    pRef.fCmdSource = srcElement;

    if (picURL == null) callBack?.ImageLoaded(pRef, false);
    else {
      // Keep the subscribers of a request already pending for this key.
      var key = this.GetKey(theDirectory, theFile);
      if (!this.fPendingIndex.has(key)) this.fPendingIndex.set(key, []);

      anImage["picmgr"] = pRef;
      anImage["orgpath"] = this.fWSMgr.GetOrgPath();
      anImage["directory"] = theDirectory;
      anImage["imagename"] = theFile;
      anImage.onload = PM_Image_Loaded;
      anImage.onerror = PM_Image_Not_Found;
      anImage.crossOrigin = "anonymous";

      this.fPicLoadCount++;
      anImage["picURL"] = picURL;

      var pq = this.fPicQueue;
      pq.push(anImage);

      if (pq.length == 1) {
        // gPicMgr = this;
        setTimeout(PM_Handle_PicQueue_Start, 10, this);
      }
    }
  }

  GetPicLoadCount() {
    return this.fPicLoadCount;
  }

  ReportPictureFault() {
    this.fPictureFault++;
  }

  ClearPictureFault() {
    this.fPictureFault = 0;
  }

  GetPictureFault() {
    return this.fPictureFault;
  }

  IsMissingImage(
    anImage,
    missingImage64,
    w,
    h // AddPicture
  ) {
    var o = anImage;
    var w1 = o.width;
    var h1 = o.height;
    if (w1 == w && h1 == h) {
      var imageData = getBase64Image(o);
      if (imageData == missingImage64) return true;
    }

    return false;
  }
}
